export default function TextVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - Text Editor - Notepad &amp; Code
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for Text Editor - Notepad
            &amp; Code.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I create a new file?
              </h3>
              <p className="mt-1">
                Tap the <strong>Quick Note</strong> button on the home screen to
                instantly start a new note, or go to the Editor tab and tap the
                + button to open a new blank file. You can choose a language
                (Plain Text, Python, JavaScript, etc.) at any time from the
                editor toolbar.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I open an existing file?
              </h3>
              <p className="mt-1">
                Tap <strong>Open File</strong> on the home screen or go to the
                Files tab and browse your device storage. Tap any supported file
                to open it in the editor. Files are processed locally and never
                uploaded to any server.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I save a file?
              </h3>
              <p className="mt-1">
                Tap the save icon in the editor toolbar. For new files, you will
                be prompted to choose a save location and filename. For already
                saved files, it saves in place. The app also auto-saves every 30
                seconds and when the app goes to the background.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I switch syntax highlighting language?
              </h3>
              <p className="mt-1">
                Tap the language label in the editor toolbar (e.g. &ldquo;Plain
                Text&rdquo;) to open the language picker. TextVEdit supports 14
                languages including Python, JavaScript, TypeScript, JSON, XML,
                Markdown, Bash, CSS, Dart, Java, Kotlin, SQL, and YAML. For
                unsaved files, the filename extension updates automatically when
                you switch language.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do multi-tabs work?
              </h3>
              <p className="mt-1">
                TextVEdit supports multiple open files at once. Each file opens
                in its own tab at the top of the editor. Tap a tab to switch,
                tap + to open another file, and swipe or tap × to close a tab.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What are Quick Notes?
              </h3>
              <p className="mt-1">
                Quick Notes let you jot something down instantly. Tap the{" "}
                <strong>Quick Note</strong> FAB on the home screen — a note
                opens immediately. Notes auto-save and appear in the Quick Notes
                list on the home screen. You can pin important notes so they
                stay at the top.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I use Find &amp; Replace?
              </h3>
              <p className="mt-1">
                In the editor, tap the search icon in the toolbar to open the
                Find &amp; Replace panel. Enter your search term and optionally
                a replacement. You can toggle case sensitivity on or off.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I change the language of the app?
              </h3>
              <p className="mt-1">
                Go to <strong>Settings</strong> and tap Language. TextVEdit
                supports 15 interface languages including English, Hindi,
                Spanish, Portuguese, Arabic, Chinese, Japanese, Korean, and
                more.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development of TextVEdit. You can
                remove all ads with a one-time in-app purchase available in
                Settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have the latest
                version from Google Play or the App Store. If the crash persists,
                email us with your device model, OS version, app version, and
                steps to reproduce.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24–48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
            <li>A sample file if relevant</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/textvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/textvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/textvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/textvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> Text Editor - Notepad &amp; Code
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.textvedit
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
